import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Calculator, CalendarDays, Plus, TrendingDown, TrendingUp, Wallet, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getClinicHealthData } from "@/lib/apiClient";

const COST_ROWS = [
  { key: "salaries_centavos", splitKey: "salaries_pct", label: "Salaries", shortLabel: "Salaries", color: "bg-indigo-500", stroke: "#6366f1" },
  { key: "rent_utilities_centavos", splitKey: "rent_pct", label: "Rent & utilities", shortLabel: "Rent", color: "bg-emerald-500", stroke: "#10b981" },
  { key: "supplies_cogs_centavos", splitKey: "supplies_pct", label: "Supplies & COGS", shortLabel: "Supplies", color: "bg-amber-500", stroke: "#f59e0b" },
  { key: "other_costs_centavos", splitKey: "other_pct", label: "Other costs", shortLabel: "Other", color: "bg-slate-400", stroke: "#94a3b8" },
];

export default function ClinicHealthCalculator() {
  const { activeClinic, activeClinicId } = useAuth();
  const navigate = useNavigate();
  const clinicId = activeClinicId || activeClinic?.clinic_id;
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeMode, setRangeMode] = useState("mtd");
  const [customRange, setCustomRange] = useState({ startDate: "", endDate: "" });
  const [selectedMetric, setSelectedMetric] = useState(null);

  const load = useCallback(async (rangeRequest = {}) => {
    if (!clinicId) {
      setError("No active clinic selected.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const response = await getClinicHealthData({ clinicId, ...rangeRequest });
    if (response?.ok === false) {
      setError(response.message || "Could not load clinic health.");
      setLoading(false);
      return;
    }
    setState(response || null);
    setLoading(false);
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  const currency = state?.currency || activeClinic?.currency || "PHP";
  const monthDate = useMemo(() => {
    if (!state?.month_key) return new Date();
    const [year, month] = state.month_key.split("-").map(Number);
    return new Date(year, month - 1, 1);
  }, [state?.month_key]);
  const overhead = state?.overhead_settings || {};
  const totalOverhead = Number(state?.total_overhead_centavos || overhead.total_monthly_overhead_centavos || 0);
  const rangeOverhead = Number(state?.range_overhead_centavos ?? state?.prorated_overhead_centavos ?? 0);
  const hasOverhead = totalOverhead > 0 || !!overhead.created_at;

  if (loading && !state) {
    return <div data-testid="clinic-health-loading" className="rounded-lg border border-slate-200 bg-white p-5 text-[13px] text-slate-500">Loading Clinic Health...</div>;
  }

  if (error) {
    return (
      <div data-testid="clinic-health-error" className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-[13px] text-rose-700">
        <div className="flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" strokeWidth={2.25} />Clinic Health could not load</div>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  if (!state) return null;

  const buildRangeRequest = (mode, nextCustomRange = customRange) => {
    if (mode === "mtd") return {};
    const asOf = parseIsoDate(state.as_of_date) || new Date();
    const monthKey = state.month_key || formatMonthKey(asOf);
    if (mode === "today") {
      const day = formatIsoDate(asOf);
      return { monthKey, startDate: day, endDate: day };
    }
    if (mode === "last7") {
      return { monthKey, startDate: formatIsoDate(addDays(asOf, -6)), endDate: formatIsoDate(asOf) };
    }
    if (mode === "last30") {
      return { monthKey, startDate: formatIsoDate(addDays(asOf, -29)), endDate: formatIsoDate(asOf) };
    }
    const startDate = nextCustomRange.startDate || `${monthKey}-01`;
    const endDate = nextCustomRange.endDate || formatIsoDate(asOf);
    return { monthKey, startDate, endDate };
  };

  const handleRangeChange = (mode) => {
    setRangeMode(mode);
    let nextCustomRange = customRange;
    if (mode === "custom" && (!customRange.startDate || !customRange.endDate)) {
      const asOf = parseIsoDate(state.as_of_date) || new Date();
      const monthKey = state.month_key || formatMonthKey(asOf);
      nextCustomRange = { startDate: `${monthKey}-01`, endDate: formatIsoDate(asOf) };
      setCustomRange(nextCustomRange);
    }
    load(buildRangeRequest(mode, nextCustomRange));
  };

  const handleCustomDateChange = (key, value) => {
    const nextCustomRange = { ...customRange, [key]: value };
    setCustomRange(nextCustomRange);
    if (nextCustomRange.startDate && nextCustomRange.endDate) {
      load(buildRangeRequest("custom", nextCustomRange));
    }
  };

  const restrictedCount = Number(state.restricted_count || 0);

  if (!hasOverhead) {
    return (
      <div data-testid="clinic-health-calculator" className="space-y-4">
        <ClinicHealthHeader state={state} monthDate={monthDate} activeClinic={activeClinic} showEdit={false} onManageExpenses={() => navigate("/expenses")} />
        <section data-testid="clinic-health-setup-prompt" className="flex min-h-[420px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <div className="max-w-md">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><Plus className="h-5 w-5" strokeWidth={2.25} /></div>
            <h2 className="mt-4 text-[20px] font-bold tracking-tight text-slate-950">Overhead now lives in Expenses</h2>
            <p className="mt-2 text-[13px] leading-6 text-slate-600">Add salaries, rent, supplies, and other monthly costs on the Expenses page. Clinic Health will compare them against collected cash for the month.</p>
            <button type="button" onClick={() => navigate("/expenses")} className="mt-5 inline-flex h-10 items-center gap-2 rounded-md bg-teal-700 px-4 text-[13px] font-bold text-white hover:bg-teal-800">
              <Wallet className="h-4 w-4" strokeWidth={2.25} />Manage expenses
            </button>
          </div>
        </section>
      </div>
    );
  }

  const metrics = buildMetricTiles(state, rangeMode, currency);
  const formulaText = selectedMetric ? buildFormulaText(state, rangeMode, currency, selectedMetric) : "";
  const rangeDays = Number(state.range_days || state.days_elapsed || 0);

  return (
    <div data-testid="clinic-health-calculator" className="space-y-5">
      <ClinicHealthHeader
        state={state}
        monthDate={monthDate}
        activeClinic={activeClinic}
        onManageExpenses={() => navigate("/expenses")}
        rangeMode={rangeMode}
        onRangeChange={handleRangeChange}
      />
      {restrictedCount > 0 ? (
        <p data-testid="clinic-health-restricted-note" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-500">
          {restrictedCount} expense line{restrictedCount === 1 ? " is" : "s are"} restricted and not shown, but {restrictedCount === 1 ? "is" : "are"} included in totals.
        </p>
      ) : null}
      {rangeMode === "custom" ? (
        <CustomRangeInputs customRange={customRange} onChange={handleCustomDateChange} />
      ) : null}
      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {metrics.map((metric) => {
          const { key, ...tileProps } = metric;
          return (
            <MetricTile
              key={key}
              {...tileProps}
              selected={selectedMetric === key}
              onClick={() => setSelectedMetric((current) => (current === key ? null : key))}
            />
          );
        })}
      </section>
      {selectedMetric ? <FormulaBar text={formulaText} onClose={() => setSelectedMetric(null)} /> : null}
      <RevenueVsOverheadChart data={state.daily_revenue || []} totalOverhead={rangeOverhead} daysElapsed={rangeDays} currency={currency} />
      <section className="grid gap-4 xl:grid-cols-[1fr_1.15fr]">
        <CollectionCalendar data={state.daily_revenue || []} monthDate={parseIsoDate(state.range_start_date) || monthDate} dailyTarget={Number(state.daily_overhead_target_centavos || 0)} daysElapsed={rangeDays} currency={currency} />
        <CostBreakdown overhead={overhead} costSplit={state.cost_split || {}} total={totalOverhead} currency={currency} />
      </section>
      <section className="grid gap-4 lg:grid-cols-3">
        <BreakEvenCard state={state} currency={currency} totalOverhead={rangeOverhead} />
        <CostSplitDonut overhead={overhead} total={totalOverhead} currency={currency} />
        <DaysAboveTargetGrid data={state.daily_revenue || []} daysElapsed={rangeDays} dailyTarget={Number(state.daily_overhead_target_centavos || 0)} daysAbove={Number(state.days_above_target || 0)} currency={currency} />
      </section>
    </div>
  );
}

function ClinicHealthHeader({ state, monthDate, activeClinic, onManageExpenses, showEdit = true, rangeMode = "mtd", onRangeChange }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="text-[13px] font-semibold text-slate-500">{state.clinic_name || activeClinic?.clinic_name || "Clinic"} · {monthDate.toLocaleDateString("en-PH", { month: "long", year: "numeric" })}</p>
        <h1 className="mt-1 text-[30px] font-bold tracking-tight text-slate-950">Clinic Health</h1>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeSelector active={rangeMode} onChange={onRangeChange} />
        {showEdit ? (
          <button type="button" onClick={onManageExpenses} className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 hover:bg-slate-50">
            <Wallet className="h-4 w-4" strokeWidth={2.25} />Manage expenses
          </button>
        ) : null}
      </div>
    </header>
  );
}

function DateRangeSelector({ active, onChange }) {
  const options = [
    { key: "today", label: "Today" },
    { key: "last7", label: "Last 7 days" },
    { key: "last30", label: "Last 30 days" },
    { key: "mtd", label: "MTD" },
    { key: "custom", label: "Custom", icon: true },
  ];
  return (
    <div data-testid="clinic-health-range-selector" className="flex h-10 items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
      {options.map((option) => {
        const selected = active === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange?.(option.key)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[13px] font-semibold transition ${selected ? "border border-slate-300 bg-white text-slate-950 shadow-sm" : "border border-transparent bg-transparent text-slate-600 hover:text-slate-950"}`}
          >
            {option.icon ? <CalendarDays className="h-3.5 w-3.5" strokeWidth={2.25} /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CustomRangeInputs({ customRange, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-600">
        From
        <input type="date" value={customRange.startDate} onChange={(event) => onChange("startDate", event.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-[13px] font-semibold text-slate-950 outline-none focus:border-teal-600" />
      </label>
      <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-600">
        To
        <input type="date" value={customRange.endDate} onChange={(event) => onChange("endDate", event.target.value)} className="h-9 rounded-md border border-slate-300 px-3 text-[13px] font-semibold text-slate-950 outline-none focus:border-teal-600" />
      </label>
    </div>
  );
}

function MetricTile({ label, value, hint, tone, selected, onClick }) {
  const className = tone === "bad" ? "border-rose-200 bg-rose-50 text-rose-900" : tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-950" : tone === "amber" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-950";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`clinic-health-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`min-h-[132px] rounded-lg border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${selected ? "border-2 shadow-sm" : ""} ${className}`}
    >
      <div className="text-[12px] font-bold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-4 text-[30px] font-bold leading-none tabular-nums tracking-tight">{value}</div>
      <div className="mt-3 text-[12px] font-semibold opacity-70">{hint}</div>
    </button>
  );
}

function FormulaBar({ text, onClose }) {
  return (
    <div data-testid="clinic-health-formula-bar" className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[13px] font-semibold leading-6 text-slate-700 shadow-sm">
      <Calculator className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" strokeWidth={2.25} />
      <p className="min-w-0 flex-1">{text}</p>
      <button type="button" onClick={onClose} aria-label="Close formula" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700">
        <X className="h-4 w-4" strokeWidth={2.25} />
      </button>
    </div>
  );
}

function RevenueVsOverheadChart({ data, totalOverhead, daysElapsed, currency }) {
  const past = useMemo(() => {
    let running = 0;
    return (data || []).filter((day) => Number(day.day_number || 0) <= Number(daysElapsed || 0)).map((day) => {
      running += Number(day.revenue_centavos || 0);
      const dayNumber = Number(day.day_number || 0);
      return { dayNumber, dayDate: day.day_date, revenue: running, overhead: Math.floor((Number(totalOverhead || 0) * dayNumber) / (data.length || 1)) };
    });
  }, [data, daysElapsed, totalOverhead]);
  const maxValue = Math.max(1, ...past.map((d) => d.revenue), ...past.map((d) => d.overhead));
  const width = 960, height = 260, pad = { left: 56, right: 18, top: 18, bottom: 34 };
  const plotW = width - pad.left - pad.right, plotH = height - pad.top - pad.bottom;
  const xFor = (day) => pad.left + ((day - 1) / Math.max(1, Number(daysElapsed || 1) - 1)) * plotW;
  const yFor = (value) => pad.top + (1 - value / maxValue) * plotH;
  const lineFor = (key) => past.map((d) => `${xFor(d.dayNumber)},${yFor(d[key])}`).join(" ");
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxValue * ratio));
  return (
    <section data-testid="clinic-health-chart" className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[16px] font-bold tracking-tight text-slate-950">Month-to-date: revenue vs. prorated overhead</h2>
        <div className="flex items-center gap-4 text-[12px] font-semibold text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-teal-500" />Revenue collected</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 border-t-2 border-dashed border-rose-500" />Prorated overhead</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-3 h-[260px] w-full" role="img" aria-label="Revenue collected compared with prorated overhead">
        {ticks.map((tick) => <g key={tick}><line x1={pad.left} x2={width - pad.right} y1={yFor(tick)} y2={yFor(tick)} stroke="#e2e8f0" /><text x={pad.left - 10} y={yFor(tick) + 4} textAnchor="end" className="fill-slate-500 text-[12px] font-semibold">{formatCompactMoney(tick, currency)}</text></g>)}
        {past.length > 1 ? <><polyline points={lineFor("overhead")} fill="none" stroke="#ef4444" strokeWidth="3" strokeDasharray="7 7" strokeLinecap="round" strokeLinejoin="round" /><polyline points={lineFor("revenue")} fill="none" stroke="#14b8a6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></> : null}
        {past.filter((_, index) => index % 3 === 0 || index === past.length - 1).map((day) => <text key={day.dayNumber} x={xFor(day.dayNumber)} y={height - 8} textAnchor="middle" className="fill-slate-500 text-[12px] font-semibold">{formatDayLabel(day.dayDate)}</text>)}
      </svg>
    </section>
  );
}

function CollectionCalendar({ data, monthDate, dailyTarget, daysElapsed, currency }) {
  const firstDay = monthDate.getDay();
  const leading = firstDay === 0 ? 6 : firstDay - 1;
  const cells = [...Array(leading).fill(null), ...(data || [])];
  return (
    <section data-testid="clinic-health-calendar" className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-[16px] font-bold tracking-tight text-slate-950">Collection calendar</h2><div className="flex items-center gap-3 text-[12px] font-semibold text-slate-500"><Legend color="bg-teal-500" label="Above" /><Legend color="bg-orange-300" label="Partial" /><Legend color="bg-slate-200" label="None" /></div></div>
      <div className="mt-4 grid grid-cols-7 gap-2 text-center text-[12px] font-bold text-slate-400">{["M", "T", "W", "T", "F", "S", "S"].map((day, index) => <div key={`${day}-${index}`}>{day}</div>)}</div>
      <div className="mt-2 grid grid-cols-7 gap-2">{cells.map((day, index) => !day ? <div key={`blank-${index}`} className="aspect-[1.4] rounded-md" /> : <CalendarDay key={day.day_number} day={day} />)}</div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-[13px] font-semibold text-slate-500"><span>Days with collection</span><span className="text-slate-950">{(data || []).filter((day) => Number(day.day_number) <= daysElapsed && Number(day.revenue_centavos || 0) > 0).length} of {daysElapsed}</span></div>
      <p className="mt-1 text-[11px] font-semibold text-slate-400">Daily target: {formatMoney(dailyTarget, currency)}</p>
    </section>
  );
}

function CalendarDay({ day }) {
  const classes = { above: "bg-teal-500 text-white border-teal-500", partial: "bg-orange-300 text-orange-950 border-orange-300", none: "bg-slate-100 text-slate-500 border-slate-100", future: "border-dashed border-slate-300 bg-white text-slate-400" };
  const displayDay = day.day_date ? new Date(`${day.day_date}T00:00:00`).getDate() : day.day_number;
  return <div className={`flex aspect-[1.4] items-center justify-center rounded-md border text-[13px] font-bold tabular-nums ${classes[day.collection_status] || classes.none}`}>{displayDay}</div>;
}

function Legend({ color, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${color}`} />{label}</span>;
}

function CostBreakdown({ overhead, costSplit, total, currency }) {
  return (
    <section data-testid="clinic-health-cost-breakdown" className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-[16px] font-bold tracking-tight text-slate-950">Cost breakdown</h2>
      <div className="mt-5 space-y-4">{COST_ROWS.map((row) => { const value = Number(overhead?.[row.key] || 0); const pct = Number(costSplit?.[row.splitKey] || 0); return <div key={row.key}><div className="flex items-baseline justify-between gap-3"><span className="text-[15px] font-bold text-slate-800">{row.label}</span><span className="text-[15px] font-bold tabular-nums text-slate-950">{formatMoney(value, currency)}</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${row.color}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></div></div>; })}</div>
      <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4"><span className="text-[15px] font-bold text-slate-700">Total monthly</span><span className="text-[18px] font-bold tabular-nums text-slate-950">{formatMoney(total, currency)}</span></div>
    </section>
  );
}

function BreakEvenCard({ state, currency, totalOverhead }) {
  const gap = Math.max(0, Number(totalOverhead || 0) - Number(state.revenue_collected_centavos || 0));
  const remaining = Number(state.days_remaining || 0);
  const progress = totalOverhead > 0 ? Math.min(100, (Number(state.revenue_collected_centavos || 0) / totalOverhead) * 100) : 0;
  return <article data-testid="clinic-health-break-even" className="rounded-lg border border-slate-200 bg-white p-4"><div className="flex items-center justify-between"><div className="text-[12px] font-bold uppercase tracking-wide text-slate-500">Break-even needed</div><TrendingUp className="h-4 w-4 text-slate-400" strokeWidth={2.25} /></div><div className="mt-5 flex items-end gap-2"><span className="text-[34px] font-bold leading-none tracking-tight text-slate-950">{formatMoney(state.break_even_needed_per_day_centavos, currency)}</span><span className="pb-1 text-[13px] font-semibold text-slate-500">per day</span></div><div className="mt-6 flex items-center gap-3"><div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-rose-500" style={{ width: `${progress}%` }} /></div><span className="text-[12px] font-semibold text-slate-500">{remaining} days left</span></div><p className="mt-4 text-[12px] font-semibold text-slate-500">{formatMoney(gap, currency)} gap / {remaining || 0} remaining days</p></article>;
}

function CostSplitDonut({ overhead, total, currency }) {
  const radius = 38, circumference = 2 * Math.PI * radius;
  let offset = 0;
  return <article data-testid="clinic-health-cost-split" className="rounded-lg border border-slate-200 bg-white p-4"><div className="text-[12px] font-bold uppercase tracking-wide text-slate-500">Cost split</div><div className="mt-3 flex items-center gap-4"><svg viewBox="0 0 100 100" className="h-28 w-28 shrink-0" role="img" aria-label="Cost split donut"><circle cx="50" cy="50" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="16" />{COST_ROWS.map((row) => { const value = Number(overhead?.[row.key] || 0); const pct = total > 0 ? value / total : 0; const dash = pct * circumference; const segment = <circle key={row.key} cx="50" cy="50" r={radius} fill="none" stroke={row.stroke} strokeWidth="16" strokeDasharray={`${dash} ${circumference - dash}`} strokeDashoffset={-offset} transform="rotate(-90 50 50)" />; offset += dash; return segment; })}</svg><div className="min-w-0 flex-1 space-y-2">{COST_ROWS.map((row) => { const value = Number(overhead?.[row.key] || 0); const pct = total > 0 ? Math.round((value * 100) / total) : 0; return <div key={row.key} className="flex items-center justify-between gap-2 text-[13px]"><span className="inline-flex min-w-0 items-center gap-2 font-semibold text-slate-700"><span className={`h-2.5 w-2.5 rounded-sm ${row.color}`} /><span className="truncate">{row.shortLabel}</span></span><span className="font-bold tabular-nums text-slate-950">{pct}%</span></div>; })}<div className="pt-2 text-[11px] font-semibold text-slate-400">{formatMoney(total, currency)} total</div></div></div></article>;
}

function DaysAboveTargetGrid({ data, daysElapsed, dailyTarget, daysAbove, currency }) {
  const past = (data || []).filter((day) => Number(day.day_number) <= daysElapsed);
  return <article data-testid="clinic-health-days-above" className="rounded-lg border border-slate-200 bg-white p-4"><div className="flex items-center justify-between"><div className="text-[12px] font-bold uppercase tracking-wide text-slate-500">Days above target</div><TrendingDown className="h-4 w-4 text-slate-400" strokeWidth={2.25} /></div><div className="mt-3 flex items-baseline gap-2"><span className="text-[34px] font-bold leading-none tracking-tight text-slate-950">{daysAbove}</span><span className="text-[13px] font-semibold text-slate-500">of {daysElapsed} days</span></div><div className="mt-5 grid grid-cols-12 gap-1.5">{past.map((day) => <span key={day.day_number} className={`h-4 rounded-sm ${day.collection_status === "above" ? "bg-teal-500" : day.collection_status === "partial" ? "bg-orange-300" : "bg-slate-200"}`} title={`Day ${day.day_number}`} />)}</div><p className="mt-4 text-[12px] font-semibold text-slate-500">Green = day beat {formatMoney(dailyTarget, currency)} target</p></article>;
}

function buildMetricTiles(state, rangeMode, currency) {
  const revenue = Number(state.revenue_collected_centavos || 0);
  const overhead = Number(state.range_overhead_centavos ?? state.prorated_overhead_centavos ?? 0);
  const shortfall = Number(state.shortfall_centavos ?? Math.max(0, overhead - revenue));
  const surplus = Number(state.surplus_centavos ?? Math.max(0, revenue - overhead));
  const days = Number(state.range_days || state.days_elapsed || 0);
  const dailyAverage = Number(state.daily_average_centavos ?? state.revenue_pace_centavos ?? 0);
  const dailyTarget = Number(state.daily_overhead_target_centavos || 0);
  const isMtd = rangeMode === "mtd";
  const revenueSubtext = {
    today: "collected today",
    last7: "collected last 7 days",
    last30: "collected last 30 days",
    mtd: "collected this month",
    custom: "collected in range",
  }[rangeMode] || "collected this month";
  const overheadSubtext = isMtd
    ? `prorated ${state.days_elapsed || days} of ${state.days_in_month || 0} days`
    : rangeMode === "today"
      ? "1 day of overhead"
      : rangeMode === "last7"
        ? "7 days of overhead"
        : rangeMode === "last30"
          ? "30 days of overhead"
          : `cost for ${days} days`;
  const gapTile = surplus > 0
    ? { key: "gap", label: "SURPLUS", value: formatMoney(surplus, currency), hint: "revenue above overhead", tone: "good" }
    : { key: "gap", label: "SHORTFALL", value: formatMoney(shortfall, currency), hint: "overhead not covered", tone: "bad" };
  const fifthTile = isMtd
    ? shortfall > 0
      ? {
          key: "periodTarget",
          label: "NEED PER DAY",
          value: formatMoney(state.need_per_day_centavos ?? state.break_even_needed_per_day_centavos ?? 0, currency),
          hint: `to clear shortfall by ${formatMonthEndLabel(state.month_key)}`,
          tone: "bad",
        }
      : { key: "periodTarget", label: "NEED PER DAY", value: "You're covered", hint: "revenue above overhead", tone: "good" }
    : buildVsTargetTile(dailyAverage, dailyTarget, currency);
  return [
    { key: "revenue", label: "REVENUE", value: formatMoney(revenue, currency), hint: revenueSubtext },
    { key: "overhead", label: "OVERHEAD", value: formatMoney(overhead, currency), hint: overheadSubtext },
    gapTile,
    { key: "dailyAverage", label: "DAILY AVERAGE", value: formatMoney(dailyAverage, currency), hint: "avg per day" },
    fifthTile,
  ];
}

function buildVsTargetTile(dailyAverage, dailyTarget, currency) {
  const difference = dailyAverage - dailyTarget;
  const targetText = formatMoney(dailyTarget, currency);
  return {
    key: "periodTarget",
    label: "VS DAILY TARGET",
    value: formatMoney(Math.abs(difference), currency),
    hint: difference >= 0 ? `above ${targetText} target` : `below ${targetText} target`,
    tone: "amber",
  };
}

function buildFormulaText(state, rangeMode, currency, metricKey) {
  const revenue = Number(state.revenue_collected_centavos || 0);
  const monthlyTotal = Number(state.total_overhead_centavos || state.overhead_settings?.total_monthly_overhead_centavos || 0);
  const overhead = Number(state.range_overhead_centavos ?? state.prorated_overhead_centavos ?? 0);
  const shortfall = Number(state.shortfall_centavos ?? Math.max(0, overhead - revenue));
  const surplus = Number(state.surplus_centavos ?? Math.max(0, revenue - overhead));
  const days = Number(state.range_days || state.days_elapsed || 0);
  const daysElapsed = Number(state.days_elapsed || days || 0);
  const daysLeft = Number(state.days_remaining || 0);
  const daysInMonth = Number(state.days_in_month || 0);
  const dailyAverage = Number(state.daily_average_centavos ?? state.revenue_pace_centavos ?? 0);
  const dailyTarget = Number(state.daily_overhead_target_centavos || 0);
  const monthName = formatMonthName(state.month_key);
  const isMtd = rangeMode === "mtd";

  if (metricKey === "revenue") {
    return `All cash payments received ${formatSelectedPeriodPhrase(rangeMode)}. Not what was billed — only posted collections. ${formatMoney(revenue, currency)} collected.`;
  }
  if (metricKey === "overhead") {
    if (isMtd) {
      return `${formatMoney(monthlyTotal, currency)} monthly overhead × ${daysElapsed} days elapsed ÷ ${daysInMonth} days in ${monthName} = ${formatMoney(overhead, currency)}. This is how much of your monthly cost has accumulated to date.`;
    }
    return `${formatMoney(monthlyTotal, currency)} monthly overhead ÷ ${daysInMonth} days × ${days} = ${formatMoney(overhead, currency)}. Cost of running the clinic for this period.`;
  }
  if (metricKey === "gap") {
    if (surplus > 0) {
      return `Revenue (${formatMoney(revenue, currency)}) − Overhead (${formatMoney(overhead, currency)}) = ${formatMoney(surplus, currency)}. You collected more than you spent this period.`;
    }
    return `Overhead (${formatMoney(overhead, currency)}) − Revenue (${formatMoney(revenue, currency)}) = ${formatMoney(shortfall, currency)}. This is how much of your overhead is not yet covered.`;
  }
  if (metricKey === "dailyAverage") {
    return `Revenue (${formatMoney(revenue, currency)}) ÷ ${days} days = ${formatMoney(dailyAverage, currency)} per day. Your earning pace for this period.`;
  }
  if (metricKey === "periodTarget" && isMtd) {
    if (shortfall <= 0) {
      return `Revenue (${formatMoney(revenue, currency)}) − Overhead (${formatMoney(overhead, currency)}) = ${formatMoney(surplus, currency)}. You are covered for ${monthName}, so no catch-up amount is needed.`;
    }
    const needPerDay = Number(state.need_per_day_centavos ?? state.break_even_needed_per_day_centavos ?? 0);
    return `Shortfall (${formatMoney(shortfall, currency)}) ÷ days left (${daysLeft}) = ${formatMoney(needPerDay, currency)} per day. Collect this every remaining day in ${monthName} to cover incurred overhead.`;
  }
  const difference = dailyAverage - dailyTarget;
  return `Your daily average (${formatMoney(dailyAverage, currency)}) vs daily overhead target (${formatMoney(dailyTarget, currency)}). You are ${formatMoney(Math.abs(difference), currency)} ${difference >= 0 ? "above" : "short of"} the daily target.`;
}

function formatSelectedPeriodPhrase(rangeMode) {
  return {
    today: "today",
    last7: "in the last 7 days",
    last30: "in the last 30 days",
    mtd: "this month",
    custom: "in the selected range",
  }[rangeMode] || "in the selected period";
}

export function formatMoney(centavos, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : currency ? `${currency} ` : "";
  return `${symbol}${Math.round(Number(centavos || 0) / 100).toLocaleString("en-PH")}`;
}

function formatCompactMoney(centavos, currency = "PHP") {
  const amount = Number(centavos || 0) / 100;
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : "";
  if (amount >= 1000) return `${symbol}${Math.round(amount / 1000)}k`;
  return `${symbol}${Math.round(amount)}`;
}

function formatDayLabel(date) {
  if (!date) return "";
  return new Date(`${date}T00:00:00`).toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function parseIsoDate(date) {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMonthKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function formatMonthEndLabel(monthKey) {
  if (!monthKey) return "month end";
  const [year, month] = monthKey.split("-").map(Number);
  const lastDay = new Date(year, month, 0);
  return lastDay.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function formatMonthName(monthKey) {
  if (!monthKey) return "the month";
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-PH", { month: "long" });
}
